import { api, PartipostApiResponse, PartipostApiPaginationWrapper } from './'
import {
    CampaignState,
    CampaignSummaryState,
    RequestCampaignPerformancePayload,
    CampaignPerformanceState,
    CampaignProgressState,
    RequestCampaignProgressPayload,
    CampaignViews,
    SubmissionsSummaryViews,
    CAMPAIGN_STATUS_ENUM,
    RequirementState,
    UpdateRegistrationMaxSlotsPayload,
    CAMPAIGN_SUBMISSION_STATUS_ENUM,
    TemplateData,
} from '../../state/campaigns'
import { COUNTRY_ENUM, PlatformContentType } from './constants'
import { MAX_PAGE_SIZE } from 'state/review'
import { PartiposterProfile } from 'state/submissions'
import { FieldCommentState, CommentState, AddCommentRequest, ReplyCommentRequest } from 'state/comments'
import { SubmissionFilter } from 'state/filter'
import { ElementKeys } from 'components/Campaign/Template/TemplateDetail/options'

export interface CampaignsRequest {
    country?: string
    platforms?: string
    collection_type?: string
    ordering?: string
}

export interface BlockedCampaign {
    uuid: string
    title: string
    status: CAMPAIGN_STATUS_ENUM
    brand: {
        uuid: string
        name: string
        description: string
        logo_image: {
            public_id: string
            resource_type: string
        }
    }
}

export interface CampaignsResult {
    uuid: string
    brand: {
        uuid: string
        name: string
        description: string
        logo_image: {
            image: string
        }
    }
    title: string
    country: COUNTRY_ENUM
    currency: string
    status: CAMPAIGN_STATUS_ENUM
    is_private: boolean
    updated_at: string
    created_at: string
    blocked_campaigns: BlockedCampaign[]
    template_data?: TemplateData
    profile_recommendation_enabled?: boolean
}

export interface CampaignsRequest {
    url?: string
    limit?: number
    keywords?: string
    viewType?: CampaignViews
    country?: string
    team_type?: string[]
}

export type CampaignsResponse = PartipostApiPaginationWrapper<CampaignsResult[]>

export const getCampaigns = (payload: CampaignsRequest) => {
    const { url, viewType, team_type, ...rest } = payload
    const baseUrl = url ? url : 'campaigns/'
    const params: Record<string, string | number> = {
        ...rest,
        status:
            viewType === 'live'
                ? `${CAMPAIGN_STATUS_ENUM.DRAFT},${CAMPAIGN_STATUS_ENUM.FOR_EDIT},${CAMPAIGN_STATUS_ENUM.FOR_REVIEW},${CAMPAIGN_STATUS_ENUM.LIVE},${CAMPAIGN_STATUS_ENUM.PAUSED},${CAMPAIGN_STATUS_ENUM.REJECTED},${CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT}, ${CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH}`
                : CAMPAIGN_STATUS_ENUM.CLOSED,
    }

    if (team_type?.length) {
        params.team_type = team_type.join(',')
    }

    return api.get<PartipostApiResponse<CampaignsResponse>>(baseUrl, params)
}

export interface CampaignRequest {
    uuid: string
}

export const getCampaign = (payload: CampaignRequest) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<CampaignsResult>>(`campaigns/${uuid}/`)
}

export const getCampaignSummary = (payload: CampaignRequest) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<CampaignSummaryState>>(`campaigns-with-submissions/${uuid}/`)
}

export interface BlockedCampaignCheckResult {
    uuid: string
    brand: {
        uuid: string
        name: string
        description: string
        logo_image: {
            image: string
        }
    }
    title: string
    country: COUNTRY_ENUM
    status: CAMPAIGN_STATUS_ENUM
    is_private: boolean
    is_eligible_as_blocked_campaign: boolean
}

export interface BlockedCampaignCheckRequest {
    baseCampaignUuid: string
    blockedCampaignUuid: string
}

export const getBlockedCampaignCheck = (payload: BlockedCampaignCheckRequest) => {
    const { baseCampaignUuid, blockedCampaignUuid } = payload
    return api.get<PartipostApiResponse<BlockedCampaignCheckResult>>(
        `campaigns/${blockedCampaignUuid}/detail-with-eligibility-as-blocked-campaign-check/?main_campaign=${baseCampaignUuid}`,
    )
}

export interface CampaignProgress extends Omit<CampaignProgressState, 'partiposterId'> {
    partiposter_profile: PartiposterProfile
}
export type CampaignProgressResponse = PartipostApiPaginationWrapper<CampaignProgress[]>

export const getCampaignProgress = (payload: RequestCampaignProgressPayload) => {
    const { offset, uuid, keywords } = payload
    const parameters = {
        offset: offset,
        limit: MAX_PAGE_SIZE,
        keywords,
    }
    return api.get<PartipostApiResponse<CampaignProgressResponse>>(
        `campaigns/${uuid}/submission-progress-summary/`,
        parameters,
    )
}

export interface RequestExportCampaignProgressPayload {
    uuid: string
}

export const exportCampaignProgress = (payload: RequestExportCampaignProgressPayload) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse>(`campaigns/${uuid}/submission-progress-summary/export/`)
}

export interface RequestExportCampaignOverviewPayload {
    uuid: string
}

export const exportCampaignOverview = (payload: RequestExportCampaignOverviewPayload) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse>(`campaigns-with-submissions/${uuid}/export/`)
}

export interface CampaignPerformance extends Omit<CampaignPerformanceState, 'partiposterId'> {
    partiposter_profile: PartiposterProfile
}
export type CampaignPerformanceResponse = PartipostApiPaginationWrapper<CampaignPerformance[]>

export const getCampaignPerformance = (payload: RequestCampaignPerformancePayload) => {
    const { offset, uuid, keywords, tags } = payload
    const parameters = {
        offset: offset,
        limit: MAX_PAGE_SIZE,
        keywords,
        tags,
    }
    return api.get<PartipostApiResponse<CampaignPerformanceResponse>>(
        `campaigns/${uuid}/submission-performance/`,
        parameters,
    )
}

export type SubmissionFiltersResponse = {
    results: SubmissionFilter[]
}

export const getCampaignPerformanceFilters = (payload: RequestCampaignPerformancePayload) => {
    return api.get<PartipostApiResponse<SubmissionFiltersResponse>>(
        `campaigns/${payload.uuid}/submission-performance/filters/`,
    )
}

export interface RequestExportCampaignPerformancePayload {
    uuid: string
}

export const exportCampaignPerformance = (payload: RequestExportCampaignPerformancePayload) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse>(`campaigns/${uuid}/submission-performance/export/`)
}

export interface SubmissionsSummaryRequest {
    url?: string
    limit?: number
    keywords?: string
    country?: string
    viewType: SubmissionsSummaryViews
    team_type?: string[]
}

interface TaskResult {
    title: string
    total_pending_submission_count: number
    latest_submission_photo: {
        image: string
        uuid: string
        img_main: string
    }
}

export interface SubmissionsSummaryResult {
    uuid: string
    brand: {
        uuid: string
        name: string
        description: string
        logo_image: {
            image: string
        }
    }
    tasks: TaskResult[]
    title: string
    status: CAMPAIGN_STATUS_ENUM
    is_private: boolean
    created_at: string
}

export type SubmissionsSummaryResponse = PartipostApiPaginationWrapper<SubmissionsSummaryResult[]>

export const getSubmissionsSummary = (payload: SubmissionsSummaryRequest) => {
    const { url, viewType, team_type, ...rest } = payload
    const baseUrl = url ? url.replace('http:', 'https:') : 'campaigns-with-submissions/'
    const params: Record<string, string | number> = {
        ...rest,
        status: viewType === 'active' ? '2,4' : '5',
    }

    if (team_type?.length) {
        params.team_type = team_type.join(',')
    }

    return api.get<PartipostApiResponse<SubmissionsSummaryResponse>>(baseUrl, params)
}

export interface RegisterCampaignRequest {
    brand: {
        uuid?: string
    }
    auto_update_submission_status: boolean
    batching_enable: boolean
    slot_number: number | null
    slot_required: boolean
    objective: number | null
    categories: number[]
    is_private: boolean
    title: string
    budget: number | null
    registration_enable: boolean
    registration_max_slots?: number | null
    submission_status_filling_fast_min: number | null
    submission_status_full_min: number | null
    profile_recommendation_enabled?: boolean
}

export interface RegisterCampaignResponse {
    uuid: string
    brand: {
        uuid: string
        name: string
        description: string
        logo_image: {
            image: string
            img_main: string
            uuid: string
        }
    }
    objective: number
    categories: number[]
    images: string[]
    is_private: boolean | null
    title: string
    description: string | null
    requirements: RequirementState[]
    submission_status: CAMPAIGN_SUBMISSION_STATUS_ENUM
    status: number
    budget: number
    payout_deadline: any
}

export const registerCampaign = (payload: RegisterCampaignRequest) => {
    return api.post<PartipostApiResponse<RegisterCampaignResponse>>('campaigns/', payload)
}

export interface UpdateCampaignRequest {
    campaign: RegisterCampaignRequest
    uuid: string
    updateProfileRecommendationSetting?: boolean
    callback: () => void
}

export const updateCampaign = (payload: UpdateCampaignRequest) => {
    return api.patch<PartipostApiResponse<CampaignsResult>>(`campaigns/${payload.uuid}/`, payload.campaign)
}

export interface UpdateProfileRecommendationSettingResponse {
    profile_recommendation_enabled: boolean
}

export interface UpdateProfileRecommendationSettingRequest extends UpdateProfileRecommendationSettingResponse {
    uuid: string
}

export const updateProfileRecommendationSetting = (payload: UpdateProfileRecommendationSettingRequest) => {
    const { uuid, ...rest } = payload
    return api.patch<PartipostApiResponse<UpdateProfileRecommendationSettingResponse>>(
        `campaigns/${payload.uuid}/profile-recommendation-enabled/`,
        rest,
    )
}

export interface CampaignActionRequest {
    uuid: string
    onSuccessCallback?: () => void
}

export const duplicateCampaign = (payload: CampaignActionRequest) => {
    return api.post<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${payload.uuid}/duplicate/`)
}

export const deleteCampaign = (payload: CampaignActionRequest) => {
    return api.delete<PartipostApiResponse<CampaignsResult>>(`campaigns/${payload.uuid}`)
}

export interface UpdatePayoutDeadlineResponse {
    payout_deadline: string
}

export interface UpdatePayoutDeadlineRequest extends UpdatePayoutDeadlineResponse {
    showToast?: boolean
    uuid: string
}

export const updatePayoutDeadline = (payload: UpdatePayoutDeadlineRequest) => {
    const { uuid, payout_deadline } = payload
    return api.patch<PartipostApiResponse<UpdatePayoutDeadlineResponse>>(`campaigns/${uuid}/payout-deadline/`, {
        payout_deadline,
    })
}

export interface SubmitCampaignRequest {
    campaign: CampaignState
    uuid: string
}

export const submitCampaign = (payload: SubmitCampaignRequest) => {
    const { campaign, uuid } = payload
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${uuid}/save-and-submit/`, campaign)
}

export interface SendCampaignToEditRequest {
    uuid: string
}

export const sendCampaignToEdit = (payload: SendCampaignToEditRequest) => {
    const { uuid } = payload
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${uuid}/save-and-request-edit/`)
}

export interface SendCampaignToPaymentRequest {
    uuid: string
}

export const sendCampaignToPaymentRequest = (payload: SendCampaignToPaymentRequest) => {
    const { uuid } = payload
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${uuid}/request-payment/`)
}

export interface SendCampaignBackToReviewRequest {
    uuid: string
}
// it's only be used when campaign in payment request but want to back to for review state
export const sendCampaignBackToReview = (payload: SendCampaignBackToReviewRequest) => {
    const { uuid } = payload
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${uuid}/send-back-to-for-review/`)
}

export interface PublishCampaignRequest {
    campaign: CampaignState
    uuid: string
}

export const publishCampaign = (payload: PublishCampaignRequest) => {
    const { campaign, uuid } = payload
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(`campaigns/${uuid}/save-and-publish/`, campaign)
}

export interface SetCampaignSubmissionStatusRequest {
    uuid: string
    submissionStatus: CAMPAIGN_SUBMISSION_STATUS_ENUM
}

export const setCampaignSubmissionStatus = (payload: SetCampaignSubmissionStatusRequest) => {
    const { uuid, submissionStatus } = payload
    const requestBody = {
        submission_status: submissionStatus,
    }
    return api.patch<PartipostApiResponse<RegisterCampaignResponse>>(
        `campaigns/${uuid}/update-submission-status/`,
        requestBody,
    )
}

export interface SetAutoSubmissionStatusRequest {
    uuid: string
    full: number
    fillingFast: number
    batchingEnable: boolean
}
export interface SetAutoSubmissionStatusResponse {
    submission_status_filling_fast_min: number
    submission_status_full_min: number
    batching_enable: boolean
}

export const setAutoSubmissionStatus = (payload: SetAutoSubmissionStatusRequest) => {
    const { uuid, full, fillingFast, batchingEnable } = payload
    const requestBody = {
        submission_status_full_min: full,
        submission_status_filling_fast_min: fillingFast,
        batching_enable: batchingEnable,
    }
    return api.put<PartipostApiResponse<SetAutoSubmissionStatusResponse>>(
        `campaigns/${uuid}/update-submission-status-threshold/`,
        requestBody,
    )
}

export interface CloseCampaignRequest {
    uuid: string
    reason: string | undefined
    forceClose: boolean
}

export const closeCampaign = (payload: CloseCampaignRequest) => {
    const { uuid, reason, forceClose } = payload
    const requestBody = {
        reason,
        force_close: forceClose,
    }
    return api.patch<PartipostApiResponse<CampaignsResult>>(`campaigns/${uuid}/close/`, requestBody)
}

export const updateRegistrationMaxSlots = (payload: UpdateRegistrationMaxSlotsPayload) => {
    const { uuid, max_slots } = payload
    const requestBody = {
        max_slots: max_slots,
    }
    return api.patch<PartipostApiResponse<CampaignsResult>>(
        `campaigns/${uuid}/registration-forms/max-slots/`,
        requestBody,
    )
}

export interface GetCampaignCommentsRequest extends CampaignRequest {
    identifier?: string
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}

export const getCampaignComments = (payload: GetCampaignCommentsRequest) => {
    const { uuid, ...rest } = payload
    return api.get<PartipostApiResponse<FieldCommentState[]>>(`campaigns/${uuid}/comments`, rest)
}

export const addCampaignComment = (payload: AddCommentRequest) => {
    const { uuid, ...rest } = payload
    return api.post<PartipostApiResponse<CommentState>>(`campaigns/${uuid}/comments/`, { parent: null, ...rest })
}

export const replyCampaignComment = (payload: ReplyCommentRequest) => {
    const { uuid, commentUuid, ...rest } = payload
    return api.post<PartipostApiResponse<CommentState>>(`campaigns/${uuid}/comments/${commentUuid}/replies/`, rest)
}

export interface PatchCommentRequest extends CampaignRequest {
    field_identifier: string
    commentUuid: string
    comment: string
    onSuccessCallback: () => void
    onErrorCallback: () => void
}

export const updateCampaignComment = (payload: PatchCommentRequest) => {
    const { uuid, commentUuid } = payload
    return api.patch<PartipostApiResponse<CommentState>>(`campaigns/${uuid}/comments/${commentUuid}/`, {
        comment: payload.comment,
    })
}

export interface DeleteCommentRequest extends CampaignRequest {
    commentUuid: string
    isThread?: boolean
    onSuccessCallback: () => void
    onErrorCallback: () => void
}

export const deleteCampaignComment = (payload: DeleteCommentRequest) => {
    const { uuid, commentUuid, isThread } = payload
    return api.delete<PartipostApiResponse<CommentState>>(
        `campaigns/${uuid}/comments/${commentUuid}/${isThread ? 'thread/' : ''}`,
    )
}

export interface ResolveThreadRequest extends CampaignRequest {
    commentUuid: string
    resolved: boolean
    identifier: string
    threadId: string
}

export const resolveThread = (payload: ResolveThreadRequest) => {
    const { uuid, commentUuid, resolved } = payload
    return api.patch<PartipostApiResponse<{ resolved: boolean }>>(
        `campaigns/${uuid}/comments/${commentUuid}/resolve/`,
        {
            resolved: resolved,
        },
    )
}

export interface UpdateTotalEarningPayload {
    campaignUuid: string
    submissionUuid: string
    total_earnings: number
    reason: string
    custom_reason: string
    payout_remark?: string
    adjustment_source: string
}

export type UpdateTotalEarningResponse = Omit<UpdateTotalEarningPayload, 'campaignUuid' | 'submissionUuid'>

export const updateTotalEarning = (payload: UpdateTotalEarningPayload) => {
    const { campaignUuid, submissionUuid, ...restPayload } = payload
    return api.put<PartipostApiResponse<UpdateTotalEarningResponse>>(
        `campaigns/${campaignUuid}/submission-performance/${submissionUuid}/adjust-earnings/`,
        restPayload,
    )
}

export interface PaidOutsideAppPayload {
    campaignUuid: string
    submissionUuid: string
    total_earnings: number
    reason: string
    custom_reason: string
    payout_remark?: string
    payment_method: string
    custom_payment_method: string
}

export type PaidOutsideAppResponse = Omit<PaidOutsideAppPayload, 'campaignUuid' | 'submissionUuid'>

export const paidOutsideApp = (payload: PaidOutsideAppPayload) => {
    const { campaignUuid, submissionUuid, ...restPayload } = payload
    return api.put<PartipostApiResponse<PaidOutsideAppResponse>>(
        `campaigns/${campaignUuid}/submission-performance/${submissionUuid}/mark-as-paid/`,
        restPayload,
    )
}

export interface CampaignPaymentSummaryData {
    budget: number
    influencer_budget: number
    slot_number: number
    total_payout: number
    breakdown?: {
        title: string
        element_key: ElementKeys
        platform_content_type: PlatformContentType
        payout: number
        min_purchase_quantity: number | null
        shipping_fee: number | null
        contain_dynamic_payout?: boolean
    }[]
    left_over_budget: number
    campaign_title: string
    template_name: string
    partipost_fee: number
    additional_fees?: {
        uuid: string
        title: string
        amount: number
    }[]
    detailed_mode: boolean
    service_fee_percentage: number
    subtotal: number
    tax: {
        amount: number
        label: string
        percentage: number
    }
    payable_amount: number //this is campaign_payment
}

export interface SaveAdditionalPaymentPayload {
    campaignUuid: string
    additionalFees: {
        uuid: string
        title: string
        amount: number
    }[]
    isTemplate: boolean
    isDelete?: boolean
}

export const saveAdditionalPayment = (payload: SaveAdditionalPaymentPayload) => {
    const { campaignUuid, additionalFees } = payload
    return api.put<PartipostApiResponse<CampaignPaymentSummaryData>>(
        `campaigns/${campaignUuid}/payment-adjustment/`,
        additionalFees,
    )
}

export interface UpdateServiceFeePayload {
    campaignUuid: string
    serviceFee: number
    isTemplate: boolean
}

export const updateServiceFee = (payload: UpdateServiceFeePayload) => {
    const { campaignUuid, serviceFee } = payload
    return api.patch<PartipostApiResponse<CampaignPaymentSummaryData>>(
        `campaigns/${campaignUuid}/update-service-fee-percentage/`,
        { service_fee_percentage: serviceFee },
    )
}

export interface UpdateTaxPayload {
    campaignUuid: string
    tax: number
    isTemplate: boolean
}

export const updateTax = (payload: UpdateTaxPayload) => {
    const { campaignUuid, tax } = payload
    return api.patch<PartipostApiResponse<CampaignPaymentSummaryData>>(
        `campaigns/${campaignUuid}/update-tax-percentage/`,
        { tax_percentage: tax },
    )
}

export interface GetPaymentSummaryPayload {
    campaignUuid: string
    budget?: number
}

export const getPaymentSummary = (payload: GetPaymentSummaryPayload) => {
    const { campaignUuid, budget } = payload
    return api.get<PartipostApiResponse<CampaignPaymentSummaryData>>(`campaigns/${campaignUuid}/payment-summary/`, {
        budget,
    })
}

export interface UpdatePaymentSummaryPayload {
    campaignUuid: string
    budget: number
}

export const updatePaymentSummary = (payload: UpdatePaymentSummaryPayload) => {
    const { campaignUuid, budget } = payload
    return api.patch<PartipostApiResponse<CampaignPaymentSummaryData>>(`campaigns/${campaignUuid}/payment-summary/`, {
        budget,
    })
}
